import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMediaDevicesContext } from '../../providers/media-devices-context';
import { makeAudioContext } from '../../utils/make-audio-context';

interface AudioContextWithSinkId extends AudioContext {
  setSinkId?(deviceId: string): Promise<void>;
}

export const SpeakerTester = () => {
  const { selectedSpeaker } = useMediaDevicesContext();
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);
  const audioContextRef = useRef<AudioContextWithSinkId | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  // Clean up audio context and nodes
  const cleanupAudio = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
      audioElementRef.current = null;
    }
  }, []);

  const playTestSound = useCallback(async () => {
    try {
      cleanupAudio();

      // Create a new audio element
      const audioElement = new Audio('/beat_organ_tune.mp3');
      audioElementRef.current = audioElement;

      // Create new audio context and nodes
      const audioContext = makeAudioContext() as AudioContextWithSinkId;

      // Set the audio output device if supported
      if (
        selectedSpeaker &&
        audioContext.setSinkId &&
        selectedSpeaker !== 'default'
      ) {
        try {
          await audioContext.setSinkId(selectedSpeaker);
        } catch {
          // Silently fall back to default audio output
          console.debug('Using default audio output device');
        }
      }

      const analyser = audioContext.createAnalyser();

      // Wait for the audio to be ready
      await new Promise((resolve) => {
        audioElement.addEventListener('canplaythrough', resolve, {
          once: true,
        });
      });

      const source = audioContext.createMediaElementSource(audioElement);

      // Configure analyser for visualization only
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      // Connect nodes: source -> analyser -> destination
      source.connect(analyser);
      source.connect(audioContext.destination);

      // Store references
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      // Set up ended handler
      audioElement.addEventListener('ended', () => {
        setIsPlayingTestSound(false);
        cleanupAudio();
      });

      // Play the audio
      await audioElement.play();
      setIsPlayingTestSound(true);
    } catch (err) {
      console.error('Error playing test sound:', err);
      setIsPlayingTestSound(false);
      cleanupAudio();
    }
  }, [selectedSpeaker, cleanupAudio]);

  const stopTestSound = useCallback(() => {
    setIsPlayingTestSound(false);
    cleanupAudio();
  }, [cleanupAudio]);

  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  // Effect to handle speaker changes
  useEffect(() => {
    if (selectedSpeaker && audioContextRef.current?.setSinkId) {
      audioContextRef.current
        .setSinkId(selectedSpeaker)
        .catch((err: unknown) => {
          const name =
            typeof err === 'object' && err !== null && 'name' in err
              ? err.name
              : undefined;
          const message =
            typeof err === 'object' && err !== null && 'message' in err
              ? err.message
              : undefined;
          if (
            name === 'NotAllowedError' &&
            typeof message === 'string' &&
            message.includes('Permissions-Policy')
          ) {
            console.debug('Using default audio output device');
            return;
          }
          console.error('Error updating audio output device:', err);
        });
    }
  }, [selectedSpeaker]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        mt: 1,
      }}
    >
      <Button
        variant='outlined'
        startIcon={<VolumeUpIcon />}
        onClick={isPlayingTestSound ? stopTestSound : playTestSound}
        fullWidth
      >
        {isPlayingTestSound ? 'Stop test' : 'Test speaker'}
      </Button>
    </Box>
  );
};
